import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import { SurveyExecutionHeader } from './SurveyExecutionHeader'
import { SurveyNavActions } from './SurveyNavActions'
import { SurveyProgressBar } from './SurveyProgressBar'
import {
  surveyExecutionColumnSx,
  surveyExecutionFrameSx,
  surveyExecutionHeroBleedSx,
  surveyExecutionHeroZoneSx,
  surveyExecutionWorkspaceSx,
  surveyQuestionCardShellSx,
} from '../../styles/surveyExecutionStyles'

interface SurveyExecutionFrameProps {
  readonly name: string
  readonly description?: string | null
  readonly questionIndex: number
  readonly totalQuestions: number
  readonly estimatedCompletionMinutes?: number | null
  readonly questionCard: ReactNode
  readonly showBack: boolean
  readonly nextDisabled: boolean
  readonly nextPending: boolean
  readonly nextLabel?: string
  readonly onBack: () => void
  readonly onNext: () => void
  readonly belowNav?: ReactNode
  readonly testId?: string
}

/**
 * Figma 5530-11030 — stable survey shell after Start Survey.
 * Header, progress, nav, and card chrome stay mounted; only `questionCard` swaps.
 */
export function SurveyExecutionFrame({
  name,
  description,
  questionIndex,
  totalQuestions,
  estimatedCompletionMinutes,
  questionCard,
  showBack,
  nextDisabled,
  nextPending,
  nextLabel = 'Next',
  onBack,
  onNext,
  belowNav,
  testId = 'survey-execution-frame',
}: SurveyExecutionFrameProps) {
  return (
    <Box data-testid={testId} sx={surveyExecutionFrameSx}>
      <Box sx={surveyExecutionHeroBleedSx} data-testid="survey-execution-hero-bleed">
        <Box sx={surveyExecutionHeroZoneSx} data-testid="survey-execution-hero">
          <SurveyExecutionHeader name={name} description={description} />
        </Box>
      </Box>

      <Box sx={surveyExecutionColumnSx}>
        <Box sx={surveyExecutionWorkspaceSx} data-testid="survey-execution-workspace">
          <SurveyProgressBar
            currentIndex={questionIndex}
            total={totalQuestions}
            estimatedCompletionMinutes={estimatedCompletionMinutes}
          />

          <Box sx={surveyQuestionCardShellSx} data-testid="survey-question-card-shell">
            {questionCard}
          </Box>

          <SurveyNavActions
            showBack={showBack}
            nextDisabled={nextDisabled}
            nextPending={nextPending}
            nextLabel={nextLabel}
            onBack={onBack}
            onNext={onNext}
          />

          {belowNav}
        </Box>
      </Box>
    </Box>
  )
}
